import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/* CMM mobile promo expand fix */
(function () {
  function rmGetParts(disclosure) {
    if (!disclosure) return {};

    const trigger = disclosure.querySelector('.disclosure-trigger');
    const content = disclosure.querySelector('.disclosure-content');
    const inner = content?.querySelector('#cart-discount-disclosure');
    const form = content?.querySelector('.cart-discount__form');
    const input = content?.querySelector('.cart-discount__input');

    return { trigger, content, inner, form, input };
  }

  function rmOpenCartDiscount(disclosure) {
    const { trigger, content, inner, form, input } = rmGetParts(disclosure);

    if (!trigger || !content) return;

    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    if (!isOpen) {
      rmCloseCartDiscount(disclosure);
      return;
    }

    content.removeAttribute('inert');
    content.hidden = false;

    content.style.display = 'block';
    content.style.visibility = 'visible';
    content.style.opacity = '1';
    content.style.height = 'auto';
    content.style.maxHeight = 'none';
    content.style.blockSize = 'auto';
    content.style.overflow = 'visible';
    content.style.contentVisibility = 'visible';

    if (inner) {
      inner.style.display = 'block';
      inner.style.visibility = 'visible';
      inner.style.opacity = '1';
      inner.style.height = 'auto';
      inner.style.maxHeight = 'none';
      inner.style.blockSize = 'auto';
      inner.style.overflow = 'visible';
    }

    if (form) {
      form.style.display = 'flex';
      form.style.visibility = 'visible';
      form.style.opacity = '1';
    }

    if (input) {
      input.style.display = 'block';
      input.style.visibility = 'visible';
      input.style.opacity = '1';
      input.style.pointerEvents = 'auto';
    }
  }

  function rmCloseCartDiscount(disclosure) {
    const { content, inner, form, input } = rmGetParts(disclosure);

    if (!content) return;

    content.setAttribute('inert', '');
    content.hidden = false;

    content.style.display = '';
    content.style.visibility = '';
    content.style.opacity = '';
    content.style.height = '';
    content.style.maxHeight = '';
    content.style.blockSize = '';
    content.style.overflow = '';
    content.style.contentVisibility = '';

    if (inner) {
      inner.style.display = '';
      inner.style.visibility = '';
      inner.style.opacity = '';
      inner.style.height = '';
      inner.style.maxHeight = '';
      inner.style.blockSize = '';
      inner.style.overflow = '';
    }

    if (form) {
      form.style.display = '';
      form.style.visibility = '';
      form.style.opacity = '';
    }

    if (input) {
      input.style.display = '';
      input.style.visibility = '';
      input.style.opacity = '';
      input.style.pointerEvents = '';
    }
  }

  function rmSyncCartDiscount(disclosure) {
    const { trigger } = rmGetParts(disclosure);

    if (!trigger) return;

    if (trigger.getAttribute('aria-expanded') === 'true') {
      rmOpenCartDiscount(disclosure);
    } else {
      rmCloseCartDiscount(disclosure);
    }
  }

  function rmWatchCartDiscount(disclosure) {
    if (!disclosure || disclosure.dataset.rmPromoFixAttached === 'true') return;

    disclosure.dataset.rmPromoFixAttached = 'true';

    const { trigger } = rmGetParts(disclosure);

    if (!trigger) return;

    const observer = new MutationObserver(() => {
      rmSyncCartDiscount(disclosure);
    });

    observer.observe(trigger, {
      attributes: true,
      attributeFilter: ['aria-expanded']
    });
  }

  function rmInitCartDiscounts() {
    document
      .querySelectorAll('disclosure-custom.cart-discount')
      .forEach((disclosure) => {
        rmWatchCartDiscount(disclosure);
        rmSyncCartDiscount(disclosure);
      });
  }

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest(
      'disclosure-custom.cart-discount .disclosure-trigger'
    );

    if (!trigger) return;

    const disclosure = trigger.closest('disclosure-custom.cart-discount');

    requestAnimationFrame(() => {
      rmSyncCartDiscount(disclosure);
      setTimeout(() => rmSyncCartDiscount(disclosure), 80);
    });
  });

  document.addEventListener('DOMContentLoaded', rmInitCartDiscounts);

  customElements.whenDefined('cart-drawer-component').then(() => {
    rmInitCartDiscounts();
  });

  rmInitCartDiscounts();
})();